/**
 * Universal Business Suite App Switcher
 * 
 * Reusable component for all Executive Force AI applications
 * Provides seamless navigation between apps with SSO token passing
 * 
 * USAGE:
 * 1. Copy this file to your app's components folder
 * 2. Import and add to your app's header/navbar
 * 3. Update CURRENT_APP_ID to match your app
 * 
 * INTEGRATION:
 * - Reads JWT token from localStorage
 * - Passes token when switching apps
 * - Connects to Admin Platform for subscription data
 * - Connects to Integration Hub for app health
 */

import React, { useState, useEffect } from 'react';

// ============================================================================
// CONFIGURATION - Update this for each app
// ============================================================================

const CURRENT_APP_ID = 'crm';

// Production Cloud Run URLs
const APPS = [
  {
    id: 'forceai',
    name: 'EF-Strategy',
    url: process.env.REACT_APP_FORCEAI_URL || 'https://ef-strategy.com',
    icon: '🧠',
    description: 'AI Business Intelligence',
    color: '#8B5CF6',
  },
  {
    id: 'crm',
    name: 'EF-CRM',
    url: (process.env.REACT_APP_CRM_URL || 'https://ef-crm.com') + '/dashboard',
    icon: '👥',
    description: 'Customer Relationship Management',
    color: '#3B82F6',
  },
  {
    id: 'financial',
    name: 'EF-CapFlow',
    url: (process.env.REACT_APP_FINANCIAL_URL || 'https://ef-capflow.com') + '/dashboard',
    icon: '💰',
    description: 'US Financial Management',
    color: '#10B981',
  },
  {
    id: 'ascin',
    name: 'EF-Link',
    url: (process.env.REACT_APP_ASCIN_URL || 'https://ef-link.com') + '/dashboard',
    icon: '🚚',
    description: 'Supply Chain Intelligence',
    color: '#F59E0B',
  },
  {
    id: 'portal',
    name: 'Portal Dashboard',
    url: process.env.REACT_APP_PORTAL_URL || 'https://executiveforceai.com/portal',
    icon: '🏠',
    description: 'Main Portal & Settings',
    color: '#EC4899',
  },
];

// ============================================================================
// TYPES
// ============================================================================

interface App {
  id: string;
  name: string;
  url: string;
  icon: string;
  description: string;
  color: string;
}

interface AppSwitcherProps {
  className?: string;
  position?: 'header' | 'sidebar';
}

// ============================================================================
// COMPONENT
// ============================================================================

export const BusinessSuiteAppSwitcher: React.FC<AppSwitcherProps> = ({
  className = '',
  position = 'header',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentApp, setCurrentApp] = useState<App | null>(null);
  const [otherApps, setOtherApps] = useState<App[]>([]);

  useEffect(() => {
    // Find current app
    const current = APPS.find(app => app.id === CURRENT_APP_ID);
    setCurrentApp(current || APPS[0]);

    // Filter out current app and portal
    const others = APPS.filter(app =>
      app.id !== CURRENT_APP_ID && app.id !== 'landing'
    );
    setOtherApps(others);
  }, []);

  const handleSwitchApp = (app: App) => {
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('access_token') ||
        localStorage.getItem('jwt_token') ||
        localStorage.getItem('auth_token');

      // Build URL with token
      const targetUrl = token
        ? `${app.url}?token=${token}`
        : app.url;

      // Navigate to app
      window.location.href = targetUrl;
    } catch (error) {
      console.error('Error switching app:', error);
      // Fallback: navigate without token
      window.location.href = app.url;
    }
  };

  const handleGoToPortal = () => {
    const portalApp = APPS.find(app => app.id === 'portal');
    if (portalApp) {
      handleSwitchApp(portalApp);
    }
  };

  if (!currentApp) return null;

  // Header version (dropdown)
  if (position === 'header') {
    return (
      <div style={{ position: 'relative' }} className={className}>
        {/* Current App Button - Compact for AppBar */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 8px',
            borderRadius: '8px',
            borderLeft: `2px solid ${currentApp.color}`,
            background: 'transparent',
            border: 'none',
            borderLeftStyle: 'solid',
            borderLeftWidth: '2px',
            borderLeftColor: currentApp.color,
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          <span style={{ fontSize: '1.125rem' }}>{currentApp.icon}</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'white', lineHeight: 1.2 }}>
              {currentApp.name}
            </span>
            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.2 }}>
              Switch Apps
            </span>
          </div>
          <svg
            style={{
              width: '12px',
              height: '12px',
              transition: 'transform 0.2s',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              color: 'white',
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
              }}
              onClick={() => setIsOpen(false)}
            />

            {/* Menu */}
            <div style={{
              position: 'absolute',
              right: 0,
              marginTop: '8px',
              width: '320px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid #e5e7eb',
              zIndex: 1400,
              overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{
                padding: '12px 16px',
                background: 'linear-gradient(to right, #6366f1, #9333ea)',
                color: 'white',
              }}>
                <h3 style={{ fontWeight: 'bold', fontSize: '1.125rem', margin: 0 }}>Executive Force AI</h3>
                <p style={{ fontSize: '0.75rem', color: '#c7d2fe', margin: '4px 0 0 0' }}>Switch between your apps</p>
              </div>

              {/* Apps List */}
              <div style={{ padding: '8px', maxHeight: '384px', overflowY: 'auto' }}>
                {/* Portal Link */}
                <button
                  onClick={handleGoToPortal}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <span style={{ fontSize: '1.5rem' }}>🏠</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#111827' }}>
                      Portal Dashboard
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      Main portal & settings
                    </div>
                  </div>
                </button>

                {/* Divider */}
                <div style={{ margin: '8px 0', borderTop: '1px solid #e5e7eb' }} />

                {/* Other Apps */}
                {otherApps.map(app => (
                  <button
                    key={app.id}
                    onClick={() => handleSwitchApp(app)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      borderLeft: `3px solid ${app.color}`,
                      background: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <span style={{ fontSize: '1.5rem' }}>{app.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>
                        {app.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {app.description}
                      </div>
                    </div>
                    <svg
                      style={{ width: '16px', height: '16px', color: '#9ca3af' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>

              {/* Footer */}
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #e5e7eb',
              }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  🔐 Seamless SSO across all apps
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Sidebar version (compact)
  if (position === 'sidebar') {
    return (
      <div className={`space-y-1 ${className}`}>
        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Suite Apps
        </div>

        {/* Portal */}
        <button
          onClick={handleGoToPortal}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
        >
          <span className="text-xl">🏠</span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Portal
          </span>
        </button>

        {/* Current App (highlighted) */}
        <div
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border-l-3"
          style={{ borderLeftColor: currentApp.color, borderLeftWidth: '3px' }}
        >
          <span className="text-xl">{currentApp.icon}</span>
          <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
            {currentApp.name}
          </span>
        </div>

        {/* Other Apps */}
        {otherApps.map(app => (
          <button
            key={app.id}
            onClick={() => handleSwitchApp(app)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
          >
            <span className="text-xl">{app.icon}</span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {app.name.replace('Executive ', '').replace('Sentient ', '')}
            </span>
          </button>
        ))}
      </div>
    );
  }

  return null;
};

// ============================================================================
// INTEGRATION EXAMPLE
// ============================================================================

/**
 * HOW TO USE IN YOUR APP:
 * 
 * 1. In your Header/Navbar component:
 * 
 * ```tsx
 * import { BusinessSuiteAppSwitcher } from './components/BusinessSuiteAppSwitcher';
 * 
 * export const Header = () => {
 *   return (
 *     <header className="flex items-center justify-between px-6 py-4 bg-white shadow">
 *       <div className="flex items-center gap-4">
 *         <Logo />
 *         <BusinessSuiteAppSwitcher position="header" />
 *       </div>
 *       <UserMenu />
 *     </header>
 *   );
 * };
 * ```
 * 
 * 2. In your Sidebar component:
 * 
 * ```tsx
 * import { BusinessSuiteAppSwitcher } from './components/BusinessSuiteAppSwitcher';
 * 
 * export const Sidebar = () => {
 *   return (
 *     <aside className="w-64 bg-gray-900 text-white">
 *       <nav className="p-4">
 *         <BusinessSuiteAppSwitcher position="sidebar" className="mb-6" />
 *         {/ * Your other nav items * /}
 *       </nav>
 *     </aside>
 *   );
 * };
 * ```
 * 
 * 3. Update CURRENT_APP_ID:
 *    - Force AI: 'forceai'
 *    - CRM: 'crm'
 *    - Financial UK: 'financial'
 *    - ASCIN: 'ascin'
 */

export default BusinessSuiteAppSwitcher;
